import { useCallback, useEffect } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { OrdersClient } from "@/features/orders/OrdersClient";
import { getOrderCounts, listOrders, ordersKeys, type OrdersSearch } from "@/features/orders/orders.api";
import { orderCountParams, withBlockerFilter } from "@/features/orders/orderListParams";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { rememberListSearch } from "@/lib/listState";

export const Route = createFileRoute("/_shell/orders/")({
  // Reading is tms_orders (Transport → Orders); entering is tms_orders_write.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_orders") && !hasModule(context.session, "tms_orders_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): OrdersSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return withBlockerFilter({
      ...filterSearch(search),
      blocker: str(search.blocker),
      status: str(search.status),
      priority: str(search.priority),
      q: str(search.q),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    });
  },
  component: OrdersScreen,
});

function OrdersScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canEnter = session.roles.includes("admin") || hasModule(session, "tms_orders_write");

  // The order card and the form return to the inbox as it was left.
  useEffect(() => {
    rememberListSearch("/orders", search);
  }, [search]);

  // The Concepten tab is not a status of an order: it reads your own drafts, so the
  // order list is not asked for anything while it is open. Nor is Prijs akkoord, with
  // the prices set by hand (docs/tms-facturatie.md §24.8), nor Aanmelden (docs/tms-aanmelden.md).
  const ownList = search.status === "drafts" || search.status === "prices" || search.status === "prenotify";
  const query = useQuery({
    queryKey: ordersKeys.list(search),
    queryFn: () => listOrders(search),
    placeholderData: keepPreviousData,
    enabled: !ownList,
    // The work queue is shared: two planners work it at the same time and orders arrive
    // through EDI while it is open. Without this the list froze on whatever it showed
    // when it was opened — the app turns window-focus refetching off by default
    // (main.tsx), which for a queue is the wrong default (20-09-2026).
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // The tabs count under the same filters as the list (16-10-2026); without one this is the
  // menu's own query.
  const countParams = orderCountParams(search);
  const counts = useQuery({
    queryKey: ordersKeys.countsFor(countParams),
    queryFn: () => getOrderCounts(countParams),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Orders">
      <OrdersClient
        items={query.data?.data ?? []}
        pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
        search={search}
        counts={counts.data}
        updateParams={updateParams}
        onOpenOrder={(id) => navigate({ to: "/orders/$id", params: { id } })}
        onNewOrder={() => navigate({ to: "/orders/new" })}
        onOpenDraft={(id) => navigate({ to: "/orders/new", search: { draft: id } })}
        isFetching={query.isFetching}
        isLoading={query.isLoading && !ownList}
        canEnter={canEnter}
        userId={session.userId}
        canApprovePrice={session.roles.includes("admin") || hasModule(session, "tms_planning_write")}
      />
    </div>
  );
}
